import { getComments } from "./getComments";
import { getPcms } from "./getPcms";

// Helper function to check if a value has content
const hasContent = (value, fieldName) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.length > 0;
  if (typeof value === "object") {
    // Check if the object has any keys and that key must have value
    return (
      Object.keys(value).length > 0 &&
      Object.values(value).some((v) => v != null && v !== "")
    );
  }
  return value != null;
};

// Custom comparison function for fields with description property
const compareDescriptionField = (existing, newValue) => {
  // Only compare the description field, ignore other properties like changeRelated
  return existing.description !== newValue.description;
};

// Custom comparison function for incident summary (excluding comments)
const compareIncidentSummary = (existing, newValue) => {
  // Only compare the incidentSummary field, ignore comments and other properties
  return existing.incidentSummary !== newValue.incidentSummary;
};

// updateComplexField(
//   "precedingEvents",
//   currentObj,
//   auditObj,
//   { description: incidentState.preceEventsDescription },
//   changedFields,
//   setAuditFn
// );

// Helper function to handle complex object field updates
const updateComplexField = (
  fieldName,
  currentObj,
  auditObj,
  newValue,
  changedFields,
  setAuditFn,
  comparisonFn = null
) => {
  const existing = auditObj[fieldName];

  if (!existing) {
    // Only add to changed fields and update objects if the new value has content
    if (hasContent(newValue)) {
      changedFields.push(fieldName);
      currentObj[fieldName] = newValue;

      setAuditFn((prev) => ({
        ...prev,
        [fieldName]: newValue,
      }));
    }
    // Don't update audit object if value is empty - this prevents false positives
  } else {
    const hasChanged = comparisonFn
      ? comparisonFn(existing, newValue)
      : JSON.stringify(existing) !== JSON.stringify(newValue);

    if (hasChanged) {
      changedFields.push(fieldName);
      currentObj[fieldName] = newValue;

      setAuditFn((prev) => ({
        ...prev,
        [fieldName]: newValue,
      }));
    }
  }
};

// Helper function to normalize values for comparison
const normalizeValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") return value.trim();
  return String(value);
};

// Helper function to check if two values are actually different
const valuesAreDifferent = (existingValue, currentValue) => {
  // Handle undefined/null cases first
  const existingIsEmpty = existingValue === null || existingValue === undefined || existingValue === "";
  const currentIsEmpty = currentValue === null || currentValue === undefined || currentValue === "";
  
  // If both are empty, they're the same
  if (existingIsEmpty && currentIsEmpty) return false;
  
  // If one is empty and the other has content, they're different
  if (existingIsEmpty !== currentIsEmpty) return true;
  
  // Both have content, now normalize and compare
  const normalizedExisting = normalizeValue(existingValue);
  const normalizedCurrent = normalizeValue(currentValue);
  
  // Try to compare as numbers if both can be parsed as numbers
  const existingAsNumber = parseFloat(normalizedExisting);
  const currentAsNumber = parseFloat(normalizedCurrent);
  
  if (!isNaN(existingAsNumber) && !isNaN(currentAsNumber)) {
    // Compare as numbers with small tolerance for floating point precision
    return Math.abs(existingAsNumber - currentAsNumber) > 0.001;
  }
  
  // Compare as strings
  return normalizedExisting !== normalizedCurrent;
};

// Helper function to handle detection response fields
const updateDetectionResponse = (
  currentObj,
  auditObj,
  incidentState,
  changedFields,
  setAuditFn
) => {
  const fields = {
    description: incidentState.detectionAndResponseDescription,
    mttd: incidentState.mttd,
    mttrespond: incidentState.mttrespond,
    mttrepair: incidentState.mttrepair,
    mttrecover: incidentState.mttrecover,
  };



  const existing = auditObj.detectionResponse;

  if (!existing) {
    // When no existing data, only include fields that have content
    let detectionResponse = {};
    let hasAnyField = false;

    Object.entries(fields).forEach(([key, value]) => {
      if (hasContent(value)) {
        detectionResponse[key] = value;
        hasAnyField = true;
      }
    });

    if (hasAnyField) {
      changedFields.push("detectionResponse");
      currentObj.detectionResponse = detectionResponse;
    }

    // Set ALL fields in audit object for future comparison, preserving original types
    setAuditFn((prev) => ({
      ...prev,
      detectionResponse: {
        description: incidentState.detectionAndResponseDescription || "",
        mttd: incidentState.mttd || "",
        mttrespond: incidentState.mttrespond || "",
        mttrepair: incidentState.mttrepair || "",
        mttrecover: incidentState.mttrecover || "",
      },
    }));
  } else {
    // When existing data exists, compare each field with proper type handling
    let hasChanged = false;
    let detectionResponse = {};

    Object.entries(fields).forEach(([key, value]) => {
      const existingValue = existing[key];
      const currentValue = value;

      // Use the new comparison function that handles types properly
      if (valuesAreDifferent(existingValue, currentValue)) {
        // Only mark as changed if the new value has content OR the existing value had content
        if (hasContent(currentValue) || hasContent(existingValue)) {
          hasChanged = true;
          detectionResponse[key] = value;
        }
      }
    });

    if (hasChanged) {
      changedFields.push("detectionResponse");
      currentObj.detectionResponse = {
        ...currentObj.detectionResponse,
        ...detectionResponse,
      };

      // Update audit object with current values, preserving types
      setAuditFn((prev) => ({
        ...prev,
        detectionResponse: {
          ...prev.detectionResponse,
          description: incidentState.detectionAndResponseDescription || "",
          mttd: incidentState.mttd || "",
          mttrespond: incidentState.mttrespond || "",
          mttrepair: incidentState.mttrepair || "",
          mttrecover: incidentState.mttrecover || "",
        },
      }));
    }
  }
};

// Helper function to handle root cause updates
const updateRootCause = (
  currentObj,
  auditObj,
  rootCauseState,
  changedFields,
  setAuditFn
) => {
  const fields = {
    cause: rootCauseState.cause,
    rootCauseCode: rootCauseState.rootCauseCode,
    rootCauseSubCode: rootCauseState.rootCauseSubCode,
    causedByChangeNumber: rootCauseState.causedByChangeNumber,
    causedByVendor: rootCauseState.causedByVendor,
    vendorDetails: rootCauseState.vendorDetails,
    vendorOptions: rootCauseState.vendorDetails,
    description: rootCauseState.description,
    tags: rootCauseState.tags,
  };

  const existing = auditObj.rootCause;


  if (!existing) {
    let rootCause = {};
    let hasAnyField = false;

    Object.entries(fields).forEach(([key, value]) => {
      if (hasContent(value)) {
        rootCause[key] = value;
        hasAnyField = true;
      }
    });

    if (hasAnyField) {
      changedFields.push("rootCause");
      currentObj.rootCause = rootCause;
    }

    setAuditFn((prev) => ({
      ...prev,
      rootCause: fields,
    }));
  } else {
    let hasChanged = false;
    let rootCause = {};

    Object.entries(fields).forEach(([key, value]) => {
      const existingValue =
        key === "tags" ? existing[key]?.length : existing[key];
      const newValue = key === "tags" ? value?.length : value;

      // Check if value actually changed (handle both truthy and falsy values)
      if (String(existingValue) !== String(newValue)) {
        hasChanged = true;
        rootCause[key] = value;
      }
    });

    // Special handling: if vendorDetails or vendorOptions changed, include both to keep them in sync
    if (rootCause.vendorDetails !== undefined || rootCause.vendorOptions !== undefined) {
      rootCause.vendorDetails = fields.vendorDetails;
      rootCause.vendorOptions = fields.vendorOptions;
    }

    if (hasChanged) {
      changedFields.push("rootCause");
      currentObj.rootCause = { ...currentObj.rootCause, ...rootCause };
    }

    setAuditFn((prev) => ({
      ...prev,
      rootCause: { ...prev.rootCause, ...fields },
    }));
  }
};

// Helper function to clean up null fields
const cleanupNullFields = (obj, fieldsToCheck) => {
  fieldsToCheck.forEach((field) => {
    if (obj[field] === null) {
      delete obj[field];
    }
  });
};

// Helper function to delete specified fields
const deleteFields = (obj, fieldsToDelete) => {
  fieldsToDelete.forEach((field) => {
    delete obj[field];
  });
};

// handler(
//   currentPostmortemObject,
//   postmortemObjectForAuditing,
//   postmortemState,
//   data.changedFeilds,
//   setPostmortemObjectForAuditing
// );

// Step 0 handler - using global changedFileds from state
const handleStep0 = (
  currentObj,
  auditObj,
  postmortemState,
  changedFields,
  setAuditFn
) => {
  // Initialize fields
  currentObj.detectionResponse = null;
  currentObj.incident_summary = null;
  currentObj.precedingEvents = null;
  currentObj.businessImpact = null;

  const incidentState = postmortemState.incidentSummaryState;
  const changedFileds = postmortemState.changedFileds || {};


  // Handle incident summary - only if changed
  if (changedFileds.incSummDescription === true && hasContent(incidentState.incSummDescription)) {
    changedFields.push("incident_summary");


    currentObj.incident_summary = {
      summary: incidentState.incSummDescription,
    };
    
    setAuditFn((prev) => ({
      ...prev,
      incident_summary: {
        incidentSummary: incidentState.incSummDescription,
      },
    }));
  }

  // Handle preceding events - only if changed
  if (changedFileds.preceEventsDescription === true && hasContent(incidentState.preceEventsDescription)) {
    changedFields.push("precedingEvents");
    currentObj.precedingEvents = {
      description: incidentState.preceEventsDescription,
    };
    
    setAuditFn((prev) => ({
      ...prev,
      precedingEvents: {
        description: incidentState.preceEventsDescription,
      },
    }));
  }

  // Handle business impact - only if changed
  if (changedFileds.impact === true && hasContent(incidentState.impact)) {
    changedFields.push("businessImpact");
    currentObj.businessImpact = {
      description: incidentState.impact,
    };
    
    setAuditFn((prev) => ({
      ...prev,
      businessImpact: {
        description: incidentState.impact,
      },
    }));
  }

  // Handle detection response - check each MTT field and description
  const detectionResponseChanges = {};
  let hasDetectionChanges = false;

  if (changedFileds.detectionAndResponseDescription === true && hasContent(incidentState.detectionAndResponseDescription)) {
    detectionResponseChanges.description = incidentState.detectionAndResponseDescription;
    hasDetectionChanges = true;
  }

  if (changedFileds.mttd === true && hasContent(incidentState.mttd)) {
    detectionResponseChanges.mttd = incidentState.mttd;
    hasDetectionChanges = true;
  }

  if (changedFileds.mttrespond === true && hasContent(incidentState.mttrespond)) {
    detectionResponseChanges.mttrespond = incidentState.mttrespond;
    hasDetectionChanges = true;
  }

  if (changedFileds.mttrepair === true && hasContent(incidentState.mttrepair)) {
    detectionResponseChanges.mttrepair = incidentState.mttrepair;
    hasDetectionChanges = true;
  }

  if (changedFileds.mttrecover === true && hasContent(incidentState.mttrecover)) {
    detectionResponseChanges.mttrecover = incidentState.mttrecover;
    hasDetectionChanges = true;
  }

  if (hasDetectionChanges) {
    changedFields.push("detectionResponse");
    currentObj.detectionResponse = detectionResponseChanges;
    
    setAuditFn((prev) => ({
      ...prev,
      detectionResponse: {
        ...prev.detectionResponse,
        ...detectionResponseChanges,
      },
    }));
  }

  // Cleanup
  deleteFields(currentObj, [
    "recovery",
    "rootCauseIdentificationRequest",
    "rootCause",
    "recurrence",
    "preventiveCorrectiveMeasures",
  ]);

  cleanupNullFields(currentObj, [
    "incident_summary",
    "precedingEvents",
    "businessImpact",
    "detectionResponse",
  ]);
};

// Step 1 handler - using global changedFileds from state
const handleStep1 = (
  currentObj,
  auditObj,
  postmortemState,
  changedFields,
  setAuditFn
) => {
  // Initialize fields
  currentObj.recovery = null;
  currentObj.rootCauseIdentificationRequest = null;
  currentObj.rootCause = null;
  currentObj.recurrence = null;


  const recoveryState = postmortemState.RecoveryState;
  const recurrenceState = postmortemState.RecurrenceState;
  const rootCauseState = postmortemState.RootCauseState;
  const identificationState = postmortemState.RootCauseIdentificationState;
  const changedFileds = postmortemState.changedFileds || {};

  // Handle recovery - only if changed
  if (changedFileds.recovery === true && hasContent(recoveryState.recovery)) {
    changedFields.push("recovery");
    currentObj.recovery = {
      recovery: recoveryState.recovery,
    };
    
    setAuditFn((prev) => ({
      ...prev,
      recovery: {
        recovery: recoveryState.recovery,
      },
    }));
  }

  // Handle recurrence - only if changed


  if(changedFileds.incidentId){
    changedFields.push("recurrence");
    currentObj.recurrence = {
      pastReferences:{
        incidentId: postmortemState.RecurrenceState.incidentId
      },
      pastReferencesList: postmortemState.RecurrenceState.pastReferencesList
    }
  
  }

  if(changedFileds.problemId){
    changedFields.push("recurrence");
    currentObj.recurrence = {
      ...currentObj.recurrence,
      pastReferences:{
        ...currentObj.recurrence?.pastReferences,
        problemId: postmortemState.RecurrenceState.problemId
      } 
    }
  }

  if(changedFileds.recurringIssue){
    changedFields.push("recurrence");
    currentObj.recurrence = {
      ...currentObj.recurrence,
      recurringIssue: postmortemState.RecurrenceState.recurringIssue
    }
  }

  // Handle root cause - check individual fields
  const rootCauseChanges = {};
  let hasRootCauseChanges = false;



  // Check for both 'cause' and 'rootCause' field names to handle inconsistency
  if ((changedFileds.cause === true || changedFileds.rootCause === true) && hasContent(rootCauseState.cause)) {
 
    rootCauseChanges.cause = rootCauseState.cause;
    hasRootCauseChanges = true;
  }

  if (changedFileds.rootCauseCode === true && hasContent(rootCauseState.rootCauseCode)) {

    rootCauseChanges.rootCauseCode = rootCauseState.rootCauseCode;
    hasRootCauseChanges = true;
  }

  if (changedFileds.rootCauseSubCode === true && hasContent(rootCauseState.rootCauseSubCode)) {

    rootCauseChanges.rootCauseSubCode = rootCauseState.rootCauseSubCode;
    hasRootCauseChanges = true;
  }

  if (changedFileds.tags === true && hasContent(rootCauseState.tags)) {

    rootCauseChanges.tags = rootCauseState.tags;
    hasRootCauseChanges = true;
  }

  // if(changedFileds.changeIsRequired) {
 
  //   rootCauseChanges.changeIsRequired = rootCauseState.changeIsRequired;
  //   hasRootCauseChanges = true;
  // }


  if(changedFileds.impactedByChange) {
 
    rootCauseChanges.impactedByChange = rootCauseState.impactedByChange;
    hasRootCauseChanges = true;
  }

  // Handle causedByChangeNumber (can be empty string to clear)
  if(changedFileds.causedByChangeNumber !== undefined) {
    rootCauseChanges.causedByChangeNumber = rootCauseState.causedByChangeNumber || "";
    hasRootCauseChanges = true;
  }

  // Handle causedByVendor
  if(changedFileds.causedByVendor) {
    rootCauseChanges.causedByVendor = rootCauseState.causedByVendor;
    hasRootCauseChanges = true;
  }

  // Handle vendorDetails - always sync both vendorDetails and vendorOptions together
  if(changedFileds.vendorDetails !== undefined) {
    const vendorValue = rootCauseState.vendorDetails || "";
    rootCauseChanges.vendorDetails = vendorValue;
    rootCauseChanges.vendorOptions = vendorValue;
    hasRootCauseChanges = true;
  }

  // Handle subCategory (for Type 2 root causes)
  if(changedFileds.subCategory !== undefined) {
    rootCauseChanges.subCategory = rootCauseState.subCategory;
    hasRootCauseChanges = true;
  }



  if (hasRootCauseChanges) {
    changedFields.push("rootCause");
    currentObj.rootCause = rootCauseChanges;  // Only include fields that actually changed
    
    setAuditFn((prev) => ({
      ...prev,
      rootCause: {
        ...prev.rootCause,
        ...rootCauseChanges,  // Only spread the fields that actually changed
      },
    }));
  }

  // Handle root cause identification request
  const identificationChanges = {};
  let hasIdentificationChanges = false;

  if (changedFileds.rcidescription === true && hasContent(identificationState.description)) {

    identificationChanges.description = identificationState.description;
    hasIdentificationChanges = true;
  }

  if (changedFileds.rciquestions === true && hasContent(identificationState.questions)) {

    identificationChanges.questionarie = identificationState.questions;
    hasIdentificationChanges = true;
  }

  if (hasIdentificationChanges) {
    changedFields.push("rootCauseIdentificationRequest");
    currentObj.rootCauseIdentificationRequest = identificationChanges;
    
    setAuditFn((prev) => ({
      ...prev,
      rootCauseIdentificationRequest: {
        ...prev.rootCauseIdentificationRequest,
        ...identificationChanges,
      },
    }));
  }

  // Cleanup
  deleteFields(currentObj, [
    "incident_summary",
    "precedingEvents",
    "businessImpact",
    "detectionResponse",
    "preventiveCorrectiveMeasures",
  ]);

  cleanupNullFields(currentObj, [
    "recovery",
    "rootCauseIdentificationRequest",
    "rootCause",
    "recurrence",
  ]);
};

// Step 2 handler - using global changedFileds from state
const handleStep2 = (
  currentObj,
  auditObj,
  postmortemState,
  changedFields,
  setAuditFn
) => {
  currentObj.preventiveCorrectiveMeasures = null;

  const pcmState = postmortemState.PreventiveAndCorrectiveMeasuresState;
  const changedFileds = postmortemState.changedFileds || {};



  let pcmData = [];
  let hasChanges = false;

  // Handle pTasks - only if changed
  if (changedFileds.pTasks === true && hasContent(pcmState.pTasks)) {
    pcmData.push({ ptask: pcmState.pTasks });
    hasChanges = true;
  }

  // Handle jira tickets - only if changed
  if (changedFileds.jiraTickets === true && hasContent(pcmState.jiraTickets)) {
    const jiraObj = { jira: pcmState.jiraTickets };
    if (pcmData.length > 0) {
      pcmData[0] = { ...pcmData[0], ...jiraObj };
    } else {
      pcmData.push(jiraObj);
    }
    hasChanges = true;
  }

  if (hasChanges) {
    changedFields.push("preventiveCorrectiveMeasures");
    currentObj.preventiveCorrectiveMeasures = pcmData;

    const fullPcmData = getPcms(pcmState);
    setAuditFn((prev) => ({
      ...prev,
      preventiveCorrectiveMeasures: fullPcmData,
    }));
  }

  // Cleanup
  deleteFields(currentObj, [
    "recovery",
    "rootCauseIdentificationRequest",
    "rootCause",
    "recurrence",
    "incident_summary",
    "precedingEvents",
    "businessImpact",
    "detectionResponse",
  ]);

  cleanupNullFields(currentObj, ["preventiveCorrectiveMeasures"]);
};

export const getAuditData = (
  postmortemObjectForAuditing,
  postmortemState,
  step,
  currentPostmortemObject,
  setCurrentPostmortemObject,
  setPostmortemObjectForAuditing
) => {
  let data = {
    changedFeilds: [], // Note: keeping original typo for compatibility
    changedData: {},
  };

  const stepHandlers = {
    0: handleStep0,
    1: handleStep1,
    2: handleStep2,
  };

  const handler = stepHandlers[step] || stepHandlers[2];
  handler(
    currentPostmortemObject,
    postmortemObjectForAuditing,
    postmortemState,
    data.changedFeilds,
    setPostmortemObjectForAuditing
  );

  data.changedData = { ...currentPostmortemObject };
  return data;
};

// Helper functions (keeping existing implementations with fixes)
const getPatsks = (data) => {
  let pTasks = [];

  if (data && data.length > 0 && data[0].ptask) {
    pTasks = data[0].ptask.map((ptask) => ({
      link: ptask.link,
      ptaskid: ptask.ptaskid,
      description: ptask.description,
      type: ptask.type,
      eta: ptask.eta,
      owners: [...ptask.owners],
      assignee: ptask.assignee,
      assignment_group: ptask.assignment_group,
      status: ptask.status,
      platformPortfolio: ptask.platformPortfolio || "",
      actionCategory: ptask.actionCategory || [],
      horizontalVerticalFix: ptask.horizontalVerticalFix || null,
    }));
  }

  return pTasks;
};

const getJiraTickets = (data) => {
  let jiraTickets = [];

  if (data && data.length > 0 && data[0].jira) {
    // Fixed: was checking ptask instead of jira
    jiraTickets = data[0].jira.map((jir) => ({
      jiraid: jir.jiraid,
      description: jir.description,
      ETA: jir.ETA,
      duedate: jir.duedate,
      owners: [...jir.owners],
      assignee: jir.assignee,
      status: jir.status,
      platformPortfolio: jir.platformPortfolio || "",
      actionCategory: jir.actionCategory || [],
      horizontalVerticalFix: jir.horizontalVerticalFix || null,
    }));
  }

  return jiraTickets;
};

// Helper function to exclude comments, incidentId, postmortermId, and postmortemIncomplete from objects
const excludeComments = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  const filtered = {};
  Object.keys(obj).forEach(key => {
    if (!key.toLowerCase().includes('comment') && 
        key !== 'incidentId' && 
        key !== 'postmortermId' &&
        key !== 'postmortemIncomplete') {
      filtered[key] = obj[key];
    }
  });
  return filtered;
};
